/**
 * services/events.js
 * Сервис мероприятий.
 *
 * Данные приходят с бэкенда — он раз в день скачивает их с
 * https://ibconsult.org/events/ (см. ibc-backend/scripts/scrape_events.py)
 * и отдаёт через:
 *   GET /api/v1/events/upcoming
 *   GET /api/v1/events/past
 *
 * Формат одного мероприятия:
 *   {
 *     title:        string,
 *     description:  string | null,  // короткое описание со страницы /events/
 *     image:        string | null,  // ссылка на превью
 *     date:         string | null,  // как написано на сайте, например "1-4 September 2026"
 *     location:     string | null,
 *     is_top_event: boolean,
 *     url:          string | null,  // ссылка на страницу мероприятия
 *   }
 */
import { apiService } from './api';

let _upcomingCache = null;
let _pastCache     = null;

async function _fetchUpcoming() {
  if (_upcomingCache) return _upcomingCache;
  try {
    const data = await apiService.get('/events/upcoming');
    _upcomingCache = data.events || [];
  } catch (err) {
    console.error('[events] Не удалось загрузить upcoming events:', err);
    _upcomingCache = [];
  }
  return _upcomingCache;
}

async function _fetchPast() {
  if (_pastCache) return _pastCache;
  try {
    const data = await apiService.get('/events/past');
    _pastCache = data.events || [];
  } catch (err) {
    console.error('[events] Не удалось загрузить past events:', err);
    _pastCache = [];
  }
  return _pastCache;
}

/** Присваивает стабильный id на основе url (или title, если url отсутствует) */
function _withId(event) {
  const id = event.url || event.title;
  return { ...event, id };
}

export const eventsService = {
  /**
   * Получить все предстоящие мероприятия.
   */
  async getAll() {
    const upcoming = await _fetchUpcoming();
    return upcoming.map(_withId);
  },

  /**
   * Подобрать мероприятия для показа пользователю.
   */
  async getRecommended() {
    const all = await eventsService.getAll();

    const sorted = [...all].sort((a, b) => {
      if (a.is_top_event === b.is_top_event) return 0;
      return a.is_top_event ? -1 : 1;
    });

    return sorted.map(event => ({
      ...event,
      displayName: event.title,
      displayDesc: event.description,
    }));
  },

  /**
   * Получить мероприятие по ID (url).
   */
  async getById(id) {
    const all = await eventsService.getAll();
    return all.find(e => e.id === id) || null;
  },

  /**
   * Получить прошедшие мероприятия (для будущих экранов "архив мероприятий").
   */
  async getPast() {
    const past = await _fetchPast();
    return past.map(_withId);
  },
};
